import { currencyFormatter } from "@fla/common";
import { EditIcon, useNames } from "@fla/components";
import HouseIcon from "@fla/components/src/assets/svgs/house.svg";
import SellIcon from "@fla/components/src/assets/svgs/sell.svg";
import { RealEstate_InitialState } from "@fla/components/src/interfaces";
import { Fragment } from "react";

type Props = { realEstateData: RealEstate_InitialState[] } & ReturnType<typeof useNames>;

export function useRealEstate({
  realEstateData,
  spouseFirstName,
  userFirstName,
  getName,
  getAlternateName,
  getBankDetails
}: Props) {
  const sumMortgagesValue = (mortgages: { value: string }[] = []) => {
    return mortgages.reduce((p, c) => p + Number(c.value), 0);
  };

  const userTotalAmount = (() => {
    return realEstateData
      .filter(
        data =>
          (data.property_status === "sell" &&
            data.sale_proceeds &&
            ["50_50", "split_by_percentage", "something_else"].includes(data.sale_proceeds)) ||
          (data.property_status === "sell" &&
            data.sale_proceeds === "one_keeps_all" &&
            data.sale_proceeds_owner === "user") ||
          (data.property_status === "one_keeps" && data.property_keeper === "user") ||
          (data.property_status === "one_keeps" &&
            data.property_keeper === "spouse" &&
            data.if_paying_settlement === "yes")
      )
      .reduce((prev, curr) => {
        const netEquity =
          Number(curr.value) -
          (Array.isArray(curr?.mortgage) ? curr?.mortgage?.reduce((p: number, c) => p + Number(c.value), 0) : 0);

        const totalPayment = curr?.payments?.reduce((p: number, c) => p + Number(c.value), 0) || 0;

        const totalUserPayment =
          (Array.isArray(curr?.payments)
            ? curr.payments?.filter(v => v.payment_to === "user").reduce((p: number, c) => p + Number(c.value), 0)
            : 0) +
          ((netEquity - totalPayment) * Number(curr.split_balance.user)) / 100;

        // FOR SELL
        if (curr.property_status === "sell" && curr.sale_proceeds === "50_50") {
          return prev + netEquity / 2;
        }

        if (curr.property_status === "sell" && curr.sale_proceeds === "split_by_percentage") {
          return prev + (netEquity * Number(curr.split_by_percentage.user)) / 100;
        }
        if (curr.property_status === "sell" && curr.sale_proceeds === "something_else") {
          return prev + totalUserPayment;
        }

        if (curr.property_status === "sell" && curr.sale_proceeds === "one_keeps_all") {
          return prev + netEquity;
        }

        // FOR KEEPING
        if (
          curr.property_status === "one_keeps" &&
          curr.property_keeper === "spouse" &&
          curr.payment_method === "something_else"
        ) {
          return prev + Number(curr.other_person_amount);
        }
        if (
          curr.property_status === "one_keeps" &&
          curr.property_keeper === "user" &&
          curr.payment_method === "something_else"
        ) {
          return prev + (netEquity - Number(curr.other_person_amount));
        }
        if (
          curr.property_status === "one_keeps" &&
          curr.property_keeper === "spouse" &&
          curr.payment_method === "fixed_amount"
        ) {
          return prev + Number(curr.payment_by_fixed);
        }
        if (
          curr.property_status === "one_keeps" &&
          curr.property_keeper === "user" &&
          curr.payment_method === "fixed_amount"
        ) {
          return prev + (netEquity - Number(curr.payment_by_fixed));
        }
        if (curr.property_status === "one_keeps" && curr.payment_method === "percentage") {
          return prev + (netEquity * Number(curr.payment_by_percentage.user)) / 100;
        }
        if (
          curr.property_status === "one_keeps" &&
          curr.property_keeper === "user" &&
          curr.if_paying_settlement === "yes"
        ) {
          return prev - Number(curr.payment_by_fixed);
        }
        if (curr.property_status === "one_keeps" && curr.if_paying_settlement === "no") return prev + netEquity;

        return prev;
      }, 0);
  })();

  const spouseTotalAmount = (() => {
    return realEstateData
      .filter(
        data =>
          (data.property_status === "sell" &&
            data.sale_proceeds &&
            ["50_50", "split_by_percentage", "something_else"].includes(data.sale_proceeds)) ||
          (data.property_status === "sell" &&
            data.sale_proceeds === "one_keeps_all" &&
            data.sale_proceeds_owner === "spouse") ||
          (data.property_status === "one_keeps" && data.property_keeper === "spouse") ||
          (data.property_status === "one_keeps" &&
            data.property_keeper === "user" &&
            data.if_paying_settlement === "yes")
      )
      .reduce((prev, curr) => {
        const netEquity =
          Number(curr.value) -
          (Array.isArray(curr?.mortgage) ? curr.mortgage.reduce((p, c) => p + Number(c.value), 0) : 0);

        const totalPayment = Array.isArray(curr?.payments)
          ? curr?.payments?.reduce((p, c) => p + Number(c.value), 0)
          : 0;

        const totalSpousePayment =
          (curr?.payments?.filter(v => v.payment_to === "spouse").reduce((p, c) => p + Number(c.value), 0) || 0) +
          ((netEquity - totalPayment) * Number(curr.split_balance.spouse)) / 100;

        // const totalThirdPartiesPayment =
        //   curr?.payments
        //     .filter((v) => v.payment_to === "something_else")
        //     .reduce((p, c) => p + Number(c.value), 0) || 0;

        if (curr.property_status === "sell" && curr.sale_proceeds === "50_50") {
          return prev + netEquity / 2;
        }

        if (curr.property_status === "sell" && curr.sale_proceeds === "split_by_percentage") {
          return prev + (netEquity * Number(curr.split_by_percentage.spouse)) / 100;
        }
        if (curr.property_status === "sell" && curr.sale_proceeds === "something_else") {
          return prev + totalSpousePayment;
        }
        if (curr.property_status === "sell" && curr.sale_proceeds === "one_keeps_all") {
          return prev + netEquity;
        }

        if (
          curr.property_status === "one_keeps" &&
          curr.property_keeper === "user" &&
          curr.payment_method === "something_else"
        ) {
          return prev + Number(curr.other_person_amount);
        }

        if (
          curr.property_status === "one_keeps" &&
          curr.property_keeper === "spouse" &&
          curr.payment_method === "something_else"
        ) {
          return prev + (netEquity - Number(curr.other_person_amount));
        }

        if (
          curr.property_status === "one_keeps" &&
          curr.property_keeper === "user" &&
          curr.payment_method === "fixed_amount"
        ) {
          return prev + Number(curr.payment_by_fixed);
        }
        if (
          curr.property_status === "one_keeps" &&
          curr.property_keeper === "spouse" &&
          curr.payment_method === "fixed_amount"
        ) {
          return prev + (netEquity - Number(curr.payment_by_fixed));
        }
        if (
          curr.property_status === "one_keeps" &&
          // curr.property_keeper === "user" &&
          curr.payment_method === "percentage"
        ) {
          return prev + (netEquity * Number(curr.payment_by_percentage.spouse)) / 100;
        }
        if (curr.property_status === "one_keeps" && curr.if_paying_settlement === "no") return prev + netEquity;

        return prev;
      }, 0);
  })();

  /**
   *
   * @param {*} userType i.e. "user" | "spouse"
   * @param {*} editData function
   */
  const sellCard = (userType: "user" | "spouse", editData?: any) => {
    return Array.isArray(realEstateData) && realEstateData.length
      ? realEstateData
          .filter(
            data =>
              (data.property_status === "sell" &&
                data.sale_proceeds &&
                ["50_50", "split_by_percentage", "something_else"].includes(data.sale_proceeds)) ||
              (data.property_status === "sell" &&
                data.sale_proceeds === "one_keeps_all" &&
                data.sale_proceeds_owner === userType)
          )
          .map((data, idx) => {
            const netEquity =
              Number(data.value) -
              (Array.isArray(data?.mortgage) ? data?.mortgage.reduce((p, c) => p + Number(c.value), 0) : 0);

            const totalPayment = data?.payments?.reduce((p, c) => p + Number(c.value), 0) || 0;

            const totalUserPayment =
              (data?.payments?.filter(v => v.payment_to === "user").reduce((p, c) => p + Number(c.value), 0) || 0) +
              ((netEquity - totalPayment) * Number(data.split_balance.user)) / 100;

            const totalSpousePayment =
              (data?.payments?.filter(v => v.payment_to === "spouse").reduce((p, c) => p + Number(c.value), 0) || 0) +
              ((netEquity - totalPayment) * Number(data.split_balance.spouse)) / 100;

            const totalThirdPartiesPayment =
              data?.payments?.filter(v => v.payment_to === "something_else").reduce((p, c) => p + Number(c.value), 0) ||
              0;

            return (
              <article key={+idx} className="bg-white rounded-lg p-5 shadow-md">
                <section className="grid grid-cols-8 gap-y-4 md:gap-y-0 mb-2">
                  <div className="col-span-2 md:col-span-1">
                    <img src={HouseIcon} className="w-12 h-auto my-3" alt="real estate" />
                  </div>
                  <div className="col-span-6 md:col-span-5">
                    <p className="text-lg font-semibold">{`${data.street}, ${data.suburb}, ${data.state}`}</p>

                    <div className="space-y-1">
                      {data.if_mortgage_on_property === "yes" ? (
                        <p className="text-sm">
                          Mortgage to:&nbsp;
                          {data?.mortgage?.map(data => getBankDetails(data.lender!)?.label).join(", ")}
                        </p>
                      ) : null}
                      {data.if_mortgage_on_property === "yes" && Array.isArray(data?.mortgage) ? (
                        <p className="text-sm">
                          Total Mortgage:&nbsp;
                          {currencyFormatter.format(sumMortgagesValue(data.mortgage as []))}
                        </p>
                      ) : null}
                      {data.owner !== "something_else" ? (
                        <p className="text-sm">
                          Ownership:&nbsp;
                          <span className="text-gray-600 font-semibold">{getName(data.owner)}</span>
                        </p>
                      ) : null}
                    </div>
                  </div>
                  {/* </div> */}
                  <div className="flex flex-col items-end col-span-8 md:col-span-2">
                    <p className="text-xl">{currencyFormatter.format(Number(data.value))}</p>
                    <p className="text-gray-500 text-sm">Estimated Value</p>

                    <p className="text-xl mt-2">{currencyFormatter.format(netEquity)}</p>
                    <p className="text-gray-500 text-sm">Equity</p>

                    {typeof editData === "function" ? (
                      <button
                        className="transition-all hover:bg-primary/20 px-2 py-1 rounded-md text-sm text-primary font-bold uppercase mt-4 flex items-center space-x-2"
                        onClick={() => editData(data)}
                      >
                        <EditIcon classes="w-3.5 h-3.5" />
                        <p className="tracking-wide">Edit</p>
                      </button>
                    ) : null}
                  </div>
                </section>

                <hr />
                <section className="justify-between items-center mt-4 grid grid-cols-3">
                  <div>
                    <div className="md:w-fit px-3 py-1 rounded-md bg-gray-300 flex items-center">
                      <img src={SellIcon} className="w-4 h-auto mr-2" alt="sell" />
                      <p className="text-sm">To Sell</p>
                    </div>
                  </div>
                  <div className="col-span-2 flex justify-end text-right pl-2">
                    {data.sale_proceeds === "50_50" ? (
                      <div className="text-sm text-gray-500">
                        <p>
                          50% To {spouseFirstName}&nbsp;
                          {currencyFormatter.format(netEquity / 2)}
                        </p>
                        <p>
                          50% To {userFirstName}&nbsp;
                          {currencyFormatter.format(netEquity / 2)}
                        </p>
                      </div>
                    ) : null}

                    {data.sale_proceeds === "split_by_percentage" ? (
                      <div className="text-sm text-gray-500">
                        <p>
                          {data.split_by_percentage.spouse}% To&nbsp;
                          {spouseFirstName} (
                          {currencyFormatter.format((netEquity * Number(data.split_by_percentage.spouse)) / 100)})
                        </p>
                        <p>
                          {data.split_by_percentage.user}% To {userFirstName}
                          &nbsp;(
                          {currencyFormatter.format((netEquity * Number(data.split_by_percentage.user)) / 100)})
                        </p>
                      </div>
                    ) : null}

                    {data.sale_proceeds === "something_else" ? (
                      <div className="text-sm text-gray-500 flex flex-col items-end">
                        <p>
                          {spouseFirstName} receives approx&nbsp;
                          {currencyFormatter.format(totalSpousePayment)}
                        </p>
                        <p>
                          {userFirstName} receives approx&nbsp;
                          {currencyFormatter.format(totalUserPayment)}
                        </p>
                        {totalThirdPartiesPayment > 0 ? (
                          <p>
                            Approx payments to third parties&nbsp;
                            {currencyFormatter.format(totalThirdPartiesPayment)}
                          </p>
                        ) : null}
                      </div>
                    ) : null}

                    {data.sale_proceeds === "one_keeps_all" && data.sale_proceeds_owner ? (
                      <p className="text-sm text-gray-500">
                        {getName(data.sale_proceeds_owner)} receives entire proceeds of sale
                      </p>
                    ) : null}
                  </div>
                </section>
              </article>
            );
          })
      : null;
  };

  /**
   *
   * @param {*} keeper i.e. "user" | "spouse"
   * @param {*} editData function
   */
  const keepCard = (keeper: "user" | "spouse", editData?: any, prefix = "-") => {
    return Array.isArray(realEstateData) && realEstateData.length
      ? realEstateData
          .filter(data => data.property_status === "one_keeps" && data.property_keeper === keeper)
          .map((data, idx) => {
            const netEquity =
              Number(data.value) -
              (Array.isArray(data?.mortgage) ? data?.mortgage.reduce((p, c) => p + Number(c.value), 0) : 0);

            return (
              <Fragment key={+idx}>
                <article className="bg-white rounded-lg p-5 shadow-md">
                  <section className="grid grid-cols-8 gap-y-4 md:gap-y-0 mb-2">
                    {/* <div className="flex space-x-4"> */}
                    <div className="col-span-2 md:col-span-1">
                      <img src={HouseIcon} className="w-12 h-auto my-3" alt="real estate" />
                    </div>
                    <div className="col-span-6 md:col-span-5">
                      <p className="text-lg font-semibold">{`${data.street}, ${data.suburb}, ${data.state}`}</p>

                      <div className="space-y-1">
                        {data.if_mortgage_on_property === "yes" && Array.isArray(data?.mortgage) ? (
                          <p className="text-sm">
                            Mortgage to:&nbsp;
                            {data.mortgage.map(data => getBankDetails(data.lender!).label).join(", ")}
                          </p>
                        ) : null}
                        {data.if_mortgage_on_property === "yes" && Array.isArray(data?.mortgage) ? (
                          <p className="text-sm">
                            Total Mortgage:&nbsp;
                            {currencyFormatter.format(sumMortgagesValue(data?.mortgage as []))}
                          </p>
                        ) : null}
                        {data.owner !== "something_else" ? (
                          <p className="text-sm">
                            Ownership:&nbsp;
                            <span className="text-gray-600 font-semibold">{getName(data.owner)}</span>
                          </p>
                        ) : null}
                      </div>
                      {/* {data.current_owner === "something_else" ? (
                        <p className="line-clamp-1 text-sm mb-3">
                          {data.current_owner_details}
                        </p>
                      ) : null} */}
                    </div>
                    {/* </div> */}
                    <div className="flex flex-col items-end col-span-8 md:col-span-2">
                      <p className="text-xl">{currencyFormatter.format(Number(data.value))}</p>
                      <p className="text-gray-500 text-sm">Estimated Value</p>

                      <p className="text-xl mt-2">{currencyFormatter.format(netEquity)}</p>
                      <p className="text-gray-500 text-sm">Equity</p>

                      {typeof editData === "function" ? (
                        <button
                          className="transition-all hover:bg-primary/20 px-2 py-1 rounded-md text-sm text-primary font-bold uppercase mt-4 flex items-center space-x-2"
                          onClick={() => editData(data)}
                        >
                          <EditIcon classes="w-3.5 h-3.5" />
                          <p className="tracking-wide">Edit</p>
                        </button>
                      ) : null}
                    </div>
                  </section>

                  <hr />
                  <section className="grid grid-cols-2 gap-x-5 mt-4 text-sm text-gray-500">
                    <div className="flex flex-col items-start col-span-2 md:col-span-1">
                      <p>Title Transferred to: {getName(data.property_keeper!)}</p>
                      <p>Mortgage responsibility of: {getName(data.property_keeper!)}</p>
                    </div>
                    <div className="flex flex-col md:items-end col-span-2 md:col-span-1">
                      <p className="line-clamp-2">
                        Payment to {getAlternateName(data.property_keeper!)}
                        :&nbsp;
                        {data.payment_method === "fixed_amount"
                          ? currencyFormatter.format(Number(data.payment_by_fixed))
                          : data.payment_method === "percentage"
                          ? `${data.payment_by_percentage.spouse}% of ${currencyFormatter.format(netEquity)}`
                          : `${data.payment_details} (approx ${currencyFormatter.format(
                              Number(data.other_person_amount)
                            )})`}
                      </p>
                      <p>Transfer costs paid by: {getName(data.payer!)}</p>
                    </div>
                  </section>
                </article>

                {data.if_paying_settlement === "yes" ? (
                  <div key={idx} className="rounded-xl shadow-md bg-white p-5 mb-5 grid grid-cols-8 gap-y-4 md:gap-y-0">
                    <div className="col-span-2 md:col-span-1 flex items-center">
                      <div className="w-12 h-12 bg-gray-300 rounded-full flex justify-center items-center text-gray-500 text-2xl">
                        $
                      </div>
                    </div>
                    <div className="col-span-6 md:col-span-5 pr-5">
                      <p className="text-lg font-medium">Paying to {getAlternateName(keeper)}</p>
                      <p className="text-sm text-gray-500">{`${data.street}, ${data.suburb}, ${data.state}`}</p>

                      {data.payment_details ? (
                        <p className="text-sm line-clamp-3 text-gray-500">
                          Pay of the Capital gains liability:&nbsp;
                          {data.payment_details}
                        </p>
                      ) : null}
                    </div>
                    <div className="col-span-8 md:col-span-2 text-xl flex justify-end">
                      {prefix}
                      {data.payment_method === "something_else"
                        ? currencyFormatter.format(Number(data.other_person_amount))
                        : data.payment_method === "fixed_amount"
                        ? currencyFormatter.format(Number(data.payment_by_fixed))
                        : currencyFormatter.format(
                            (netEquity * (100 - Number(data.payment_by_percentage[keeper]))) / 100
                          )}
                    </div>
                  </div>
                ) : null}
              </Fragment>
            );
          })
      : null;
  };

  /**
   *
   * @param {*} payor i.e. "user" | "spouse"
   * @returns
   */
  const paymentReceivingFromCard = (payor: "user" | "spouse") => {
    return Array.isArray(realEstateData) && realEstateData.length
      ? realEstateData
          .filter(
            data =>
              data.property_status === "one_keeps" &&
              data.property_keeper === payor &&
              data.if_paying_settlement === "yes"
          )
          .map((data, idx) => {
            const netEquity =
              Number(data.value) -
              (Array.isArray(data?.mortgage) ? data?.mortgage.reduce((p, c) => p + Number(c.value), 0) : 0);

            return (
              <div key={idx} className="rounded-xl shadow-md bg-white p-5 mb-5 grid grid-cols-8 gap-y-4 md:gap-y-0">
                <div className="col-span-2 md:col-span-1 flex items-center">
                  <div className="w-12 h-12 bg-gray-300 rounded-full flex justify-center items-center text-gray-500 text-2xl">
                    $
                  </div>
                </div>
                <div className="col-span-6 md:col-span-5 pr-5">
                  <p className="text-lg font-medium">
                    Payment from {getName(payor)} to&nbsp;
                    {getAlternateName(payor)}
                  </p>
                  <p className="text-sm text-gray-500">{`${data.street}, ${data.suburb}, ${data.state}`}</p>

                  {data.payment_details ? (
                    <p className="text-sm line-clamp-3 text-gray-500">
                      Pay of the Capital gains liability:&nbsp;
                      {data.payment_details}
                    </p>
                  ) : null}
                </div>
                <div className="col-span-8 md:col-span-2 text-xl flex justify-end">
                  {data.payment_method === "something_else"
                    ? currencyFormatter.format(Number(data.other_person_amount))
                    : data.payment_method === "fixed_amount"
                    ? currencyFormatter.format(Number(data.payment_by_fixed))
                    : currencyFormatter.format((netEquity * (100 - Number(data.payment_by_percentage[payor]))) / 100)}
                </div>
              </div>
            );
          })
      : null;
  };

  /**
   *
   * @param {*} payee i.e. "user" | "spouse"
   * @returns
   */
  const payingToCard = (payee: "user" | "spouse", prefix = "-") => {
    const payor = payee === "user" ? "spouse" : "user";

    return Array.isArray(realEstateData) && realEstateData.length
      ? realEstateData
          .filter(
            data =>
              data.property_status === "one_keeps" &&
              data.property_keeper !== payee &&
              data.if_paying_settlement === "yes"
          )
          .map((data, idx) => {
            const netEquity =
              Number(data.value) -
              (Array.isArray(data?.mortgage) ? data?.mortgage.reduce((p, c) => p + Number(c.value), 0) : 0);

            return (
              <div key={idx} className="rounded-xl shadow-md bg-white p-5 mb-5 grid grid-cols-8 gap-y-4 md:gap-y-0">
                <div className="col-span-2 md:col-span-1 flex items-center">
                  <div className="w-12 h-12 bg-gray-300 rounded-full flex justify-center items-center text-gray-500 text-2xl">
                    $
                  </div>
                </div>
                <div className="col-span-6 md:col-span-5 pr-5">
                  <p className="text-lg font-medium">Paying to {getName(payee)}</p>
                  <p className="text-sm text-gray-500">{`${data.street}, ${data.suburb}, ${data.state}`}</p>

                  {data.payment_details ? (
                    <p className="text-sm line-clamp-3 text-gray-500">
                      Pay of the Capital gains liability:&nbsp;
                      {data.payment_details}
                    </p>
                  ) : null}
                </div>
                <div className="col-span-8 md:col-span-2 text-xl flex justify-end">
                  {prefix}
                  {data.payment_method === "something_else"
                    ? currencyFormatter.format(Number(data.other_person_amount))
                    : data.payment_method === "fixed_amount"
                    ? currencyFormatter.format(Number(data.payment_by_fixed))
                    : currencyFormatter.format((netEquity * (100 - Number(data.payment_by_percentage[payor]))) / 100)}
                </div>
              </div>
            );
          })
      : null;
  };

  return {
    sellCard,
    keepCard,
    paymentReceivingFromCard,
    payingToCard,
    userTotalAmount: userTotalAmount,
    spouseTotalAmount: spouseTotalAmount,
    realEstateData
  };
}
